import axios from 'axios';
import { accountingApi } from '../../../config/apiClient';
import { ACCOUNTING_API } from '../../../config/api';
import type { ProductionEnvelope, RoutingDetail, RoutingEstimate, SaveRoutingBody } from '../types/production.types';
import { ProductionApiError } from './productionService';

const fallbackMessage = 'Beklenmeyen bir hata oluştu.';

function unwrap<T>(body: ProductionEnvelope<T> | undefined): T {
  if (!body || body.success === false || body.data === undefined) {
    throw new ProductionApiError(body?.message || fallbackMessage, body?.code);
  }
  return body.data;
}

function toApiError(error: unknown): ProductionApiError {
  if (error instanceof ProductionApiError) return error;
  if (axios.isAxiosError(error)) {
    const data = error.response?.data;
    if (data && typeof data === 'object') {
      const body = data as ProductionEnvelope<unknown>;
      return new ProductionApiError(body.message || fallbackMessage, body.code);
    }
  }
  return new ProductionApiError(fallbackMessage);
}

async function request<T>(run: () => Promise<{ data: ProductionEnvelope<T> }>): Promise<T> {
  try {
    const response = await run();
    return unwrap(response.data);
  } catch (error) {
    throw toApiError(error);
  }
}

export function getRouting(recipeId: string) {
  return request<RoutingDetail>(() => accountingApi.get(ACCOUNTING_API.PRODUCTION_ROUTING(recipeId)));
}

export function saveRouting(recipeId: string, body: SaveRoutingBody) {
  return request<RoutingDetail>(() => accountingApi.post(ACCOUNTING_API.PRODUCTION_ROUTING_SAVE(recipeId), body));
}

export function deleteRouting(recipeId: string) {
  return request<boolean>(() => accountingApi.post(ACCOUNTING_API.PRODUCTION_ROUTING_DELETE(recipeId)));
}

export function copyRouting(recipeId: string, sourceRecipeId: string) {
  return request<RoutingDetail>(() =>
    accountingApi.post(ACCOUNTING_API.PRODUCTION_ROUTING_COPY(recipeId, sourceRecipeId)));
}

export function estimateRouting(recipeId: string, quantity?: number) {
  const params: Record<string, number> = {};
  if (quantity != null && quantity > 0) params.quantity = quantity;
  return request<RoutingEstimate>(() =>
    accountingApi.get(ACCOUNTING_API.PRODUCTION_ROUTING_ESTIMATE(recipeId), { params }));
}
